/**
 * Cart Service - Enhanced with Inventory Validation Error Handling
 * Based on your current cartServices.js with added inventory validation features
 */

import { apiService, tokenManager } from "./api.js";
import { appInsights } from "../appInsights.js";
import useSpecialPricingStore, { getSpecialUnitPrice, getSpecialUnitFeeSuppressed } from "../store/specialPricingStore.js";

class CartService {
  constructor() {
    this.currentDraftOrder = null;
    this.cache = new Map();
    this.cacheTimeout = 2 * 60 * 1000; // 2 minutes
    this.currentStoreId = null; // Track current store for cache invalidation

    // Track pending changes for optimistic updates
    this.pendingChanges = {
      added: new Map(), // productSkuVariantId -> cartItem
      updated: new Map(), // itemId -> cartItem
      deleted: new Set(), // itemIds
    };

    // NEW: Track validation errors and retry logic
    this.validationErrors = new Map(); // productId -> error info
    this.retryAttempts = new Map(); // operation -> attempt count
  }

  /**
   * Get current user, client, and store info for cart operations
   */
  _getCurrentUserInfo() {
    const user = tokenManager.getUser();
    const company = tokenManager.getCurrentCompany();
    const store = tokenManager.getCurrentStore();

    if (!store?.id) {
      throw new Error(
        "No store selected. Please select a store to manage your cart.",
      );
    }

    return {
      userId: user?.id,
      companyId: company?.id,
      clientId: store.id, // Use store ID as client ID
      storeId: store.id,
      storeName: store.name,
    };
  }

  /**
   * Check if store has changed and reset cache if needed
   */
  _checkStoreChange() {
    const currentStore = tokenManager.getCurrentStore();
    const currentStoreId = currentStore?.id;

    if (this.currentStoreId !== currentStoreId) {
      console.log(
        `Store changed from ${this.currentStoreId} to ${currentStoreId}, resetting cart cache`,
      );
      this.reset();
      this.currentStoreId = currentStoreId;
    }
  }

  /**
   * Clear pending changes and validation errors
   */
  _clearPendingChanges() {
    this.pendingChanges.added.clear();
    this.pendingChanges.updated.clear();
    this.pendingChanges.deleted.clear();
    // NEW: Clear validation errors
    this.validationErrors.clear();
    this.retryAttempts.clear();
  }

  /**
   * NEW: Extract inventory error information from API error
   * Supports both the old 'errors' array format and the new 'details' array format
   */
  _parseInventoryError(error) {
    // First, check for the new structured 'details' format from checkout API
    // Format: { status: "Error", message: "Insufficient inventory", details: [...] }
    if (error?.details && Array.isArray(error.details) && error.details.length > 0) {
      return {
        type: 'insufficient_inventory',
        message: error.message || 'Insufficient inventory',
        details: error.details.map(detail => ({
          productSkuVariantId: detail.productSKUVariantID,
          requestedQuantity: detail.requestedQuantity,
          availableQuantity: detail.availableQuantity,
          message: detail.message
        }))
      };
    }

    // Check for the old 'errors' array format
    if (!error?.errors || !Array.isArray(error.errors)) {
      return null;
    }

    // Look for inventory error pattern: "Insufficient inventory for ProductSKUVariant 18292. Insufficient inventory. Available: 100, Requested: 1024"
    const inventoryErrorPattern = /Insufficient inventory for ProductSKUVariant (\d+).*Available: (\d+), Requested: (\d+)/;

    for (const errorMsg of error.errors) {
      const match = errorMsg.match(inventoryErrorPattern);
      if (match) {
        return {
          type: 'insufficient_inventory',
          message: 'Insufficient inventory',
          details: [{
            productSkuVariantId: parseInt(match[1]),
            availableQuantity: parseInt(match[2]),
            requestedQuantity: parseInt(match[3]),
            message: errorMsg
          }]
        };
      }
    }

    return null;
  }

  /**
   * NEW: Handle inventory validation error with graceful recovery
   */
  async _handleInventoryError(inventoryError, originalOperation) {
    const { productSkuVariantId, available, requested } = inventoryError;
    
    console.warn(`Inventory validation failed for product ${productSkuVariantId}: requested ${requested}, available ${available}`);
    
    // Store validation error for UI feedback
    this.validationErrors.set(productSkuVariantId, {
      ...inventoryError,
      timestamp: Date.now(),
      originalRequest: requested,
      adjustedQuantity: available
    });

    // AUTO-CORRECT: If we have available inventory, suggest adjustment
    if (available > 0) {
      console.log(`Auto-adjusting quantity for product ${productSkuVariantId} from ${requested} to ${available}`);
      
      try {
        // Try to update with maximum available quantity
        if (originalOperation.type === 'add' || originalOperation.type === 'update') {
          await this._adjustQuantityToAvailable(productSkuVariantId, available, originalOperation);
          return { success: true, adjusted: true, newQuantity: available };
        }
      } catch (adjustError) {
        console.error('Failed to auto-adjust quantity:', adjustError);
      }
    }

    return { success: false, error: inventoryError };
  }

  /**
   * NEW: Adjust quantity to maximum available with direct API call
   */
  async _adjustQuantityToAvailable(productSkuVariantId, maxAvailable, originalOperation) {
    // Clear the failing pending change
    this.pendingChanges.added.delete(productSkuVariantId);
    this.pendingChanges.updated.forEach((value, key) => {
      if (value.productSkuVariantID === productSkuVariantId) {
        this.pendingChanges.updated.delete(key);
      }
    });

    // Add corrected quantity
    if (originalOperation.type === 'add') {
      this.pendingChanges.added.set(productSkuVariantId, {
        ...originalOperation.data,
        unitQuantity: maxAvailable
      });
    } else if (originalOperation.type === 'update') {
      this.pendingChanges.updated.set(originalOperation.itemId, {
        ...originalOperation.data,
        unitQuantity: maxAvailable
      });
    }

    // Process the corrected change
    return await this._processPendingChanges();
  }

  /**
   * NEW: Determine the original operation that caused the inventory error
   */
  _determineOriginalOperation(productSkuVariantId) {
    // Check if it was an addition
    if (this.pendingChanges.added.has(productSkuVariantId)) {
      return {
        type: 'add',
        data: this.pendingChanges.added.get(productSkuVariantId)
      };
    }

    // Check if it was an update
    for (const [itemId, item] of this.pendingChanges.updated) {
      if (item.productSkuVariantID === productSkuVariantId) {
        return {
          type: 'update',
          itemId,
          data: item
        };
      }
    }

    return { type: 'unknown', data: {} };
  }

  /**
   * NEW: Revert optimistic updates for a specific product
   */
  _revertOptimisticUpdates(productSkuVariantId) {
    this.pendingChanges.added.delete(productSkuVariantId);
    
    // Remove updates for this product
    this.pendingChanges.updated.forEach((value, key) => {
      if (value.productSkuVariantID === productSkuVariantId) {
        this.pendingChanges.updated.delete(key);
      }
    });
  }

  /**
   * Apply optimistic updates to cart data
   * FIXED: Better handling of pending changes and product data preservation
   */
  _applyOptimisticUpdates(cartData) {
    if (!cartData) return cartData;

    // Clone the cart data to avoid mutations
    const optimisticCart = {
      ...cartData,
      draftCartOrderItems: [...(cartData.draftCartOrderItems || [])],
    };

    // Apply deletions first
    if (this.pendingChanges.deleted.size > 0) {
      optimisticCart.draftCartOrderItems =
        optimisticCart.draftCartOrderItems.filter(
          (item) => !this.pendingChanges.deleted.has(item.id),
        );
    }

    // Apply updates to existing items
    this.pendingChanges.updated.forEach((pendingItem, itemId) => {
      const index = optimisticCart.draftCartOrderItems.findIndex(
        (item) => item.id === itemId,
      );
      if (index >= 0) {
        // PRESERVE existing product data, only update quantity/price fields
        optimisticCart.draftCartOrderItems[index] = {
          ...optimisticCart.draftCartOrderItems[index],
          // Only update the specific fields we care about
          unitQuantity: pendingItem.unitQuantity,
          unitPrice: pendingItem.unitPrice !== undefined ? pendingItem.unitPrice : optimisticCart.draftCartOrderItems[index].unitPrice,
          singleUnitFee: pendingItem.singleUnitFee !== undefined ? pendingItem.singleUnitFee : optimisticCart.draftCartOrderItems[index].singleUnitFee,
          isSample: pendingItem.isSample !== undefined ? pendingItem.isSample : optimisticCart.draftCartOrderItems[index].isSample,
          // PRESERVE all product data
          productSkuVariant: optimisticCart.draftCartOrderItems[index].productSkuVariant
        };
      }
    });

    // Apply additions (create temporary items OR update existing ones)
    this.pendingChanges.added.forEach((pendingItem, productSkuVariantId) => {
      // First check if this product already exists as a real item (after sync)
      const existingRealItemIndex = optimisticCart.draftCartOrderItems.findIndex(
        (item) => item.productSkuVariantID === productSkuVariantId && item.id > 0,
      );

      if (existingRealItemIndex >= 0) {
        // Product exists as real item, update its quantity instead of adding duplicate
        const existingItem = optimisticCart.draftCartOrderItems[existingRealItemIndex];
        optimisticCart.draftCartOrderItems[existingRealItemIndex] = {
          ...existingItem,
          unitQuantity: pendingItem.unitQuantity, // Use the pending quantity directly
          // PRESERVE all existing product data
          productSkuVariant: existingItem.productSkuVariant
        };
        console.log(`OPTIMISTIC: Updated existing real item ${existingItem.id} with pending quantity ${pendingItem.unitQuantity}`);
      } else {
        // Check if temporary item already exists
        const existingTempItemIndex = optimisticCart.draftCartOrderItems.findIndex(
          (item) => item.productSkuVariantID === productSkuVariantId && item.id < 0,
        );

        if (existingTempItemIndex >= 0) {
          // Update existing temporary item
          optimisticCart.draftCartOrderItems[existingTempItemIndex] = {
            ...optimisticCart.draftCartOrderItems[existingTempItemIndex],
            unitQuantity: pendingItem.unitQuantity,
            unitPrice: pendingItem.unitPrice,
            singleUnitFee: pendingItem.singleUnitFee?.toString() || "0",
            isSample: pendingItem.isSample || false,
            // PRESERVE product data
            productSkuVariant: optimisticCart.draftCartOrderItems[existingTempItemIndex].productSkuVariant || pendingItem.productSkuVariant
          };
          console.log(`OPTIMISTIC: Updated existing temp item ${optimisticCart.draftCartOrderItems[existingTempItemIndex].id}`);
        } else {
          // Create new temporary item
          const newTempItem = {
            id: -productSkuVariantId, // Use negative productSkuVariantId as temporary ID
            productSkuVariantID: productSkuVariantId,
            unitQuantity: pendingItem.unitQuantity,
            unitPrice: pendingItem.unitPrice,
            singleUnitFee: pendingItem.singleUnitFee?.toString() || "0",
            isSample: pendingItem.isSample || false,
            dateCreated: new Date().toISOString(),
            lastModified: null,
            createdByUserID: tokenManager.getUser()?.id,
            lastModifiedByUserID: null,
            // ENSURE rich product data is preserved
            productSkuVariant: {
              ...pendingItem.productSkuVariant,
              // Standardize image structure
              images: pendingItem.productSkuVariant?.images || [],
              productSKUVariantImages: pendingItem.productSkuVariant?.productSKUVariantImages || [],
              // Key product properties for calculator
              unitsPerCase: pendingItem.productSkuVariant?.unitsPerCase || 1,
              optSellByTypeID: pendingItem.productSkuVariant?.optSellByTypeID || 1,
              unitAvailable: pendingItem.productSkuVariant?.unitAvailable || 0,
              unitPrice: pendingItem.productSkuVariant?.unitPrice || pendingItem.unitPrice || 0,
              singleUnitFee: pendingItem.productSkuVariant?.singleUnitFee || pendingItem.singleUnitFee || 0,
              name: pendingItem.productSkuVariant?.name,
              brand: pendingItem.productSkuVariant?.brand,
              flavor: pendingItem.productSkuVariant?.flavor,
              // Additional product fields that might be missing
              description: pendingItem.productSkuVariant?.description || '',
              legacySKU: pendingItem.productSkuVariant?.legacySKU || ''
            }
          };
          optimisticCart.draftCartOrderItems.push(newTempItem);
          console.log(`OPTIMISTIC: Created new temp item -${productSkuVariantId} with product data:`, newTempItem.productSkuVariant?.name);
        }
      }
    });

    console.log(`OPTIMISTIC UPDATE COMPLETE: ${optimisticCart.draftCartOrderItems.length} items total`);
    return optimisticCart;
  }

  /**
   * Get or create user's draft cart order for current store
   * UPDATED: Two-call approach for accurate inventory data
   */
  async getDraftCartOrder() {
    try {
      // Check if store changed
      this._checkStoreChange();

      // Make sure the customer special-pricing overlay is available for cart math.
      useSpecialPricingStore.getState().ensureLoaded();

      if (this.currentDraftOrder) {
        // Apply optimistic updates before returning
        return this._applyOptimisticUpdates(this.currentDraftOrder);
      }

      const userInfo = this._getCurrentUserInfo();

      // STEP 1: Get my open cart from the shop-owned endpoint (customer-gated, self-scoped server-side).
      console.log(`Fetching cart for store ${userInfo.storeName} (Step 1: Cart items)`);
      const cartOrder = await apiService.get(
        `/storefront/cart?clientId=${userInfo.clientId}`,
      );

      if (cartOrder && cartOrder.id) {
        if (cartOrder.draftCartOrderItems && cartOrder.draftCartOrderItems.length > 0) {
          // STEP 2: Get current product data with inventory totals
          const productSkuVariantIds = cartOrder.draftCartOrderItems.map(item => item.productSkuVariantID);
          console.log(`Fetching product data for ${productSkuVariantIds.length} products (Step 2: Product data)`);

          const productData = await this._fetchProductDataForCart(productSkuVariantIds);

          // STEP 3: Merge cart items with fresh product data
          cartOrder.draftCartOrderItems = cartOrder.draftCartOrderItems.map(cartItem => {
            const productInfo = productData.find(p => p.id === cartItem.productSkuVariantID);
            if (productInfo) {
              return {
                ...cartItem,
                productSkuVariant: productInfo
              };
            }
            console.warn(`Product data not found for productSkuVariantID: ${cartItem.productSkuVariantID}`);
            return cartItem;
          });

          console.log(`Cart loaded with ${cartOrder.draftCartOrderItems.length} items and fresh product data`);
        }

        this.currentDraftOrder = cartOrder;
        return this._applyOptimisticUpdates(this.currentDraftOrder);
      }

      // No existing draft cart order found - return empty cart structure (lazy creation)
      // Cart will be created when first item is added
      console.log(`No existing cart for store ${userInfo.storeName} - will create on first item add`);
      return { draftCartOrderItems: [] };
    } catch (error) {
      console.error("Error getting draft cart order:", error);

      // If it's a store selection error, provide helpful message
      if (error.message?.includes("No store selected")) {
        throw new Error(
          "Please select a store before adding items to your cart.",
        );
      }

      throw error;
    }
  }

  /**
   * Fetch current product data with inventory totals for cart items
   * Uses OData 'in' operator for efficient filtering
   */
  async _fetchProductDataForCart(productSkuVariantIds) {
    if (!productSkuVariantIds || productSkuVariantIds.length === 0) {
      return [];
    }

    try {
      const companyId = this._getCurrentUserInfo().companyId;

      // Use OData 'in' operator - much cleaner than chaining 'or' conditions
      // Format: productSkuVariantID in (1, 2, 3, 4, 5)
      const idList = productSkuVariantIds.join(',');
      const idFilter = `productSkuVariantID in (${idList})`;
      const companyFilter = companyId ? `companyID eq ${companyId}` : '';
      const statusFilter = 'optStatusID eq 1'; // Only active products

      const combinedFilter = [statusFilter, companyFilter, idFilter]
        .filter(Boolean)
        .join(' and ');

      console.log(`Fetching product data for ${productSkuVariantIds.length} products`);

      const response = await apiService.get("/storefront-odata/vwStorefrontProductsWithTotals", {
        $filter: combinedFilter,
        $expand: "productSKUVariant($expand=productSKUVariantImages($expand=image),ProductSkuVariantMOQPricing)",
        $select: "productSkuVariantID,productName,flavor,brandID,brandName,productSectionID,productSectionName,unitAvailable,batchCount,companyID,optStatusID,unitPrice,discountUnitPrice,singleUnitFee,unitsPerCase,optSellByTypeID,weight,isMed,isRec,caseLabel,unitLabel,allowBackorder"
      });

      // Use productService to transform the data instead of duplicating logic
      const { default: productService } = await import('./productService.js');
      const products = (response.value || []).map(item => productService._transformProduct(item));

      console.log(`Fetched ${products.length} products with current inventory`);

      return products;
    } catch (error) {
      console.error("Error fetching product data for cart:", error);
      return [];
    }
  }

  /**
   * Create a new draft cart order for current store
   */
  async createNewDraftCartOrder() {
    try {
      const userInfo = this._getCurrentUserInfo();

      console.log(
        `Creating new cart for store ${userInfo.storeName} (Client ID: ${userInfo.clientId})`,
      );
      // Shop-owned create — server derives CompanyID + open status from the (self-scoped) store.
      const response = await apiService.post("/storefront/cart", {
        ClientID: userInfo.clientId,
      });
      this.currentDraftOrder = response;
      return this._applyOptimisticUpdates(this.currentDraftOrder);
    } catch (error) {
      console.error("Error creating new draft cart order:", error);
      throw error;
    }
  }

  /**
   * Build cart update request from current state
   */
  _buildCartUpdateRequest() {
    const userInfo = this._getCurrentUserInfo();
    const draftOrderId = this.currentDraftOrder?.id || null;

    const request = {
      DraftCartOrderID: draftOrderId,
      ClientID: userInfo.clientId,
      OptOrderStatusID: 7,
      DraftCartOrderItems: {
        Added: Array.from(this.pendingChanges.added.values()).map((item) => ({
          ProductSkuVariantID: item.productSkuVariantID,
          UnitQuantity: item.unitQuantity,
          UnitPrice: item.unitPrice,
          SingleUnitFee: item.singleUnitFee?.toString() || "0",
          IsSample: item.isSample || false,
        })),
        // Filter out temporary negative IDs and ensure proper structure
        Updated: Array.from(this.pendingChanges.updated.entries())
          .filter(([itemId, item]) => {
            // Only include real positive item IDs
            const numericId = typeof itemId === 'string' ? parseInt(itemId) : itemId;
            return numericId > 0;
          })
          .map(([itemId, item]) => ({
            DraftCartOrderItemID: typeof itemId === 'string' ? parseInt(itemId) : itemId,
            UnitQuantity: item.unitQuantity,
            UnitPrice: item.unitPrice,
            SingleUnitFee: item.singleUnitFee?.toString() || "0",
            IsSample: item.isSample || false,
          })),
        // Only include real positive item IDs for deletion
        Deleted: Array.from(this.pendingChanges.deleted).filter(
          (itemId) => {
            const numericId = typeof itemId === 'string' ? parseInt(itemId) : itemId;
            return numericId > 0;
          }
        ),
      },
    };

    console.log('CART SERVICE: Built update request:', JSON.stringify(request, null, 2));
    return request;
  }

  /**
   * ENHANCED: Process pending cart changes with inventory validation error handling
   */
  async _processPendingChanges() {
    if (
      this.pendingChanges.added.size === 0 &&
      this.pendingChanges.updated.size === 0 &&
      this.pendingChanges.deleted.size === 0
    ) {
      return this.currentDraftOrder;
    }

    const request = this._buildCartUpdateRequest();
    console.log("Processing cart changes:", JSON.stringify(request, null, 2));

    try {
      const response = await apiService.post(
        "/storefront/cart/upsert",
        request,
      );

      if (response.Success || response.success) {
        const updatedCartFromApi = response.Data || response.data;
        
        // Merge with the OPTIMISTIC cart state, not the raw existing cart
        // This ensures temporary items are found during merge
        const optimisticCart = this._applyOptimisticUpdates(this.currentDraftOrder);
        const mergedCart = this._mergeCartWithApiResponse(optimisticCart, updatedCartFromApi);
        
        this.currentDraftOrder = mergedCart;
        this._clearPendingChanges();
        return this.currentDraftOrder;
      } else {
        throw new Error(
          response.Message || response.message || "Failed to update cart",
        );
      }
    } catch (error) {
      console.error("Cart update failed:", error);

      // NEW: Handle inventory validation errors
      const inventoryError = this._parseInventoryError(error);
      if (inventoryError) {
        // Determine the original operation for context
        const originalOperation = this._determineOriginalOperation(inventoryError.productSkuVariantId);
        const result = await this._handleInventoryError(inventoryError, originalOperation);
        
        if (result.success && result.adjusted) {
          // Successfully adjusted quantity, return updated cart
          return this.currentDraftOrder;
        } else {
          // Could not auto-adjust, revert optimistic updates and throw with user-friendly error
          this._revertOptimisticUpdates(inventoryError.productSkuVariantId);
          throw new Error(`Only ${inventoryError.available} units available for this product. Your cart has been adjusted.`);
        }
      }

      // For other errors, revert all pending changes
      this._clearPendingChanges();
      throw error;
    }
  }

  /**
   * Merge minimal API response with rich existing cart data
   */
  _mergeCartWithApiResponse(existingCart, apiResponse) {
    if (!existingCart || !apiResponse) return apiResponse || existingCart;

    console.log('MERGE: Merging API response with existing cart', {
      existingItemsCount: existingCart.draftCartOrderItems?.length,
      apiItemsCount: apiResponse.draftCartOrderItems?.length
    });

    return {
      ...existingCart,
      // Update top-level cart properties from API
      id: apiResponse.id,
      lastModified: apiResponse.lastModified,
      lastModifiedByUserID: apiResponse.lastModifiedByUserID,
      
      // Merge cart items - preserve rich product data, update quantities/prices
      draftCartOrderItems: apiResponse.draftCartOrderItems.map(updatedItem => {
        console.log(`MERGE: Processing API item ${updatedItem.id} for product ${updatedItem.productSkuVariantID}`);
        
        // First, try to find by exact ID match (for existing real items)
        let existingItem = existingCart.draftCartOrderItems?.find(
          item => item.id === updatedItem.id
        );
        
        // If not found by ID, try to find by productSkuVariantID (for temporary items becoming real)
        if (!existingItem) {
          existingItem = existingCart.draftCartOrderItems?.find(
            item => item.productSkuVariantID === updatedItem.productSkuVariantID && item.id < 0
          );
          
          if (existingItem) {
            console.log(`MERGE: Found temporary item ${existingItem.id} becoming real item ${updatedItem.id}`);
          }
        }
        
        // If still not found, try to find by productSkuVariantID regardless of ID (catch-all)
        if (!existingItem) {
          existingItem = existingCart.draftCartOrderItems?.find(
            item => item.productSkuVariantID === updatedItem.productSkuVariantID
          );
          
          if (existingItem) {
            console.log(`MERGE: Found any matching item ${existingItem.id} for product ${updatedItem.productSkuVariantID}`);
          }
        }
        
        if (existingItem) {
          // CRITICAL FIX: Only update cart-specific fields, preserve ALL product data
          const mergedItem = {
            ...existingItem, // Keep everything from existing item
            // ONLY update these specific cart item fields from API
            id: updatedItem.id, // Use the new real ID
            unitPrice: updatedItem.unitPrice,
            unitQuantity: updatedItem.unitQuantity,
            singleUnitFee: updatedItem.singleUnitFee,
            isSample: updatedItem.isSample,
            lastModified: updatedItem.lastModified,
            lastModifiedByUserID: updatedItem.lastModifiedByUserID,
            // DO NOT update productSkuVariant - keep existing rich data
            productSkuVariant: existingItem.productSkuVariant
          };
          
          console.log(`MERGE: Successfully merged item ${updatedItem.id} - preserved product data: ${existingItem.productSkuVariant?.name || 'no name'}`);
          return mergedItem;
        } else {
          // New item (shouldn't happen often due to optimistic updates, but handle gracefully)
          console.log(`MERGE: New item ${updatedItem.id} not found in existing cart`);
          return updatedItem;
        }
      })
    };
  }

  /**
   * NEW: Get validation errors for UI display
   */
  getValidationErrors() {
    const errors = [];
    for (const [productId, errorInfo] of this.validationErrors) {
      errors.push({
        productId,
        ...errorInfo,
        isRecent: Date.now() - errorInfo.timestamp < 30000 // Recent if within 30 seconds
      });
    }
    return errors;
  }

  /**
   * NEW: Clear validation error for a specific product
   */
  clearValidationError(productId) {
    this.validationErrors.delete(productId);
  }

  // ... [Rest of your existing methods: addToCart, updateCartItemQuantity, etc.]
  // I'm preserving all your existing method implementations

  /**
   * Add item to cart with optimistic updates
   * Creates cart lazily on first item add
   */
  async addToCart(cartItem) {
    try {
      // Get current cart state (may be empty structure if no cart exists yet)
      await this.getDraftCartOrder();
      const userInfo = this._getCurrentUserInfo();

      // Lazy cart creation: Create cart on first item add if it doesn't exist
      if (!this.currentDraftOrder?.id) {
        console.log(`Creating cart lazily for store ${userInfo.storeName} on first item add`);
        await this.createNewDraftCartOrder();
      }

      // Check if item already exists in current cart
      const existingItem = this.currentDraftOrder?.draftCartOrderItems?.find(
        (item) => item.productSkuVariantID === cartItem.productSkuVariantID,
      );

      if (existingItem) {
        // Update existing item
        const newQuantity = existingItem.unitQuantity + cartItem.unitQuantity;
        this.pendingChanges.updated.set(existingItem.id, {
          unitQuantity: newQuantity,
          unitPrice: cartItem.unitPrice,
          singleUnitFee: cartItem.singleUnitFee,
          isSample: cartItem.isSample || false,
        });
      } else {
        // Add new item
        this.pendingChanges.added.set(cartItem.productSkuVariantID, {
          productSkuVariantID: cartItem.productSkuVariantID,
          unitQuantity: cartItem.unitQuantity,
          unitPrice: cartItem.unitPrice,
          singleUnitFee: cartItem.singleUnitFee,
          isSample: cartItem.isSample || false,
          // Include product data if available for optimistic display
          productSkuVariant: cartItem.productSkuVariant,
        });
      }

      console.log(
        `Item queued for addition to cart for store ${userInfo.storeName}`,
      );

      // Process changes in background
      this._processPendingChanges().catch((error) => {
        console.error("Failed to sync cart changes:", error);
        // Could emit an event here to show error toast
      });

      // Return optimistic cart immediately
      return this._applyOptimisticUpdates(this.currentDraftOrder);
    } catch (error) {
      console.error("Error adding to cart:", error);

      if (error.message?.includes("No store selected")) {
        throw new Error(
          "Please select a store before adding items to your cart.",
        );
      }

      throw error;
    }
  }

  /**
   * Update item quantity in cart with optimistic updates
   */
  async updateCartItemQuantity(itemId, newQuantity) {
    try {
      await this.getDraftCartOrder();
      const userInfo = this._getCurrentUserInfo();

      console.log(`CART SERVICE: updateCartItemQuantity called with itemId=${itemId}, newQuantity=${newQuantity}`);

      if (newQuantity <= 0) {
        return this.removeFromCart(itemId);
      }

      // Handle negative IDs (temporary items from optimistic updates)
      const isTemporaryItem = itemId < 0;
      
      if (isTemporaryItem) {
        // For temporary items, find by productSkuVariantID instead
        const productSkuVariantId = Math.abs(itemId); // Convert negative ID back to productSkuVariantId
        
        console.log(`Handling temporary item: itemId=${itemId}, productSkuVariantId=${productSkuVariantId}`);
        
        // Check if this item now exists as a real item (after API sync)
        const realItem = this.currentDraftOrder?.draftCartOrderItems?.find(
          item => item.productSkuVariantID === productSkuVariantId && item.id > 0
        );
        
        if (realItem) {
          console.log(`Found real item for productSkuVariantId=${productSkuVariantId}, switching to real ID=${realItem.id}`);
          
          // Clear the pending addition since we're switching to the real item
          this.pendingChanges.added.delete(productSkuVariantId);
          
          // The temporary item was synced and now has a real ID, use that instead
          return this.updateCartItemQuantity(realItem.id, newQuantity);
        }
        
        // Check if this item exists in pending additions
        const pendingAddition = this.pendingChanges.added.get(productSkuVariantId);
        if (pendingAddition) {
          // Update the pending addition
          this.pendingChanges.added.set(productSkuVariantId, {
            ...pendingAddition,
            unitQuantity: newQuantity
          });
          
          console.log(`Updated pending addition for product ${productSkuVariantId}`, { newQuantity });
          
          // Return optimistic cart immediately (don't process changes yet)
          return this._applyOptimisticUpdates(this.currentDraftOrder);
        }
        
        // If not in pending additions, try to find in optimistic cart items
        const optimisticCart = this._applyOptimisticUpdates(this.currentDraftOrder);
        const optimisticItem = optimisticCart?.draftCartOrderItems?.find(
          item => item.id === itemId
        );
        
        if (optimisticItem) {
          // Convert to a proper addition since it's a new item
          this.pendingChanges.added.set(optimisticItem.productSkuVariantID, {
            productSkuVariantID: optimisticItem.productSkuVariantID,
            unitQuantity: newQuantity,
            unitPrice: optimisticItem.unitPrice,
            singleUnitFee: optimisticItem.singleUnitFee,
            isSample: optimisticItem.isSample || false,
            productSkuVariant: optimisticItem.productSkuVariant
          });
          
          console.log(`Created pending addition from optimistic item`, { 
            productSkuVariantId: optimisticItem.productSkuVariantID, 
            newQuantity 
          });
          
          // Return optimistic cart immediately
          return this._applyOptimisticUpdates(this.currentDraftOrder);
        }
        
        // FINAL FALLBACK: Create a new pending addition if we can't find the item anywhere
        console.warn(`Temporary item ${itemId} not found anywhere, creating new pending addition`);
        
        // Try to find the product data from any source
        const existingItems = this.currentDraftOrder?.draftCartOrderItems || [];
        const similarItem = existingItems.find(item => item.productSkuVariantID === productSkuVariantId);
        
        if (similarItem) {
          // Create a new pending addition based on similar item
          this.pendingChanges.added.set(productSkuVariantId, {
            productSkuVariantID: productSkuVariantId,
            unitQuantity: newQuantity,
            unitPrice: similarItem.unitPrice,
            singleUnitFee: similarItem.singleUnitFee,
            isSample: similarItem.isSample || false,
            productSkuVariant: similarItem.productSkuVariant
          });
          
          console.log(`Created new pending addition from similar item`, { productSkuVariantId, newQuantity });
          return this._applyOptimisticUpdates(this.currentDraftOrder);
        }
        
        throw new Error(`Cannot update temporary item ${itemId} - product data not found`);
      }

      // Handle real items (positive IDs)
      const existingItem = this.currentDraftOrder?.draftCartOrderItems?.find(
        item => item.id === itemId
      );

      if (!existingItem) {
        throw new Error(`Cart item with ID ${itemId} not found`);
      }

      // Queue update with proper structure
      this.pendingChanges.updated.set(itemId, {
        unitQuantity: newQuantity,
        unitPrice: existingItem.unitPrice, // Keep existing price
        singleUnitFee: existingItem.singleUnitFee, // Keep existing fee
        isSample: existingItem.isSample || false,
      });

      console.log(
        `Item quantity update queued for store ${userInfo.storeName}`,
        { itemId, newQuantity, pendingUpdates: this.pendingChanges.updated.size }
      );

      // Process changes in background
      this._processPendingChanges().catch((error) => {
        console.error("Failed to sync cart changes:", error);
      });

      // Return optimistic cart immediately
      return this._applyOptimisticUpdates(this.currentDraftOrder);
    } catch (error) {
      console.error("Error updating cart item:", error);
      throw error;
    }
  }

  // ... [Keep all your other existing methods: removeFromCart, clearCart, etc.]
  
  /**
   * Remove item from cart with optimistic updates
   */
  async removeFromCart(itemId) {
    try {
      await this.getDraftCartOrder();
      const userInfo = this._getCurrentUserInfo();

      console.log(`CART SERVICE: removeFromCart called with itemId=${itemId}`);

      // Handle negative IDs (temporary items)
      const isTemporaryItem = itemId < 0;
      
      if (isTemporaryItem) {
        // For temporary items, remove from pending additions
        const productSkuVariantId = Math.abs(itemId);
        
        // Remove from pending additions if present
        if (this.pendingChanges.added.has(productSkuVariantId)) {
          this.pendingChanges.added.delete(productSkuVariantId);
          console.log(`Removed pending addition for product ${productSkuVariantId}`);
        }
        
        // Don't add to deleted since it was never in the database
        // Just return optimistic cart without this item
        return this._applyOptimisticUpdates(this.currentDraftOrder);
      }

      // Handle real items (positive IDs)
      // Queue deletion
      this.pendingChanges.deleted.add(itemId);

      // Remove from other pending changes if present
      this.pendingChanges.updated.delete(itemId);

      console.log(`Item removal queued for store ${userInfo.storeName}`);

      // Process changes in background
      this._processPendingChanges().catch((error) => {
        console.error("Failed to sync cart changes:", error);
      });

      // Return optimistic cart immediately
      return this._applyOptimisticUpdates(this.currentDraftOrder);
    } catch (error) {
      console.error("Error removing from cart:", error);
      throw error;
    }
  }

  /**
   * Clear cart - delete all items from the draft cart order
   */
  async clearCart() {
    try {
      await this.getDraftCartOrder();
      const userInfo = this._getCurrentUserInfo();

      if (!this.currentDraftOrder?.id) {
        console.log("No cart to clear");
        return { draftCartOrderItems: [] };
      }

      // Queue all items for deletion
      const items = this.currentDraftOrder.draftCartOrderItems || [];
      items.forEach(item => {
        if (item.id > 0) {
          this.pendingChanges.deleted.add(item.id);
        }
      });

      // Clear any pending additions/updates
      this.pendingChanges.added.clear();
      this.pendingChanges.updated.clear();

      console.log(`Clearing cart for store ${userInfo.storeName}`);

      // Process the deletions
      await this._processPendingChanges();

      return this._applyOptimisticUpdates(this.currentDraftOrder);
    } catch (error) {
      console.error("Error clearing cart:", error);
      throw error;
    }
  }

  /**
   * Checkout - Convert draft cart order to a real order with inventory allocation
   * @param {object} checkoutData - Checkout data including delivery date and notes
   * @returns {Promise<object>} Created order
   */
  async checkout(checkoutData = {}) {
    try {
      await this.getDraftCartOrder();
      const userInfo = this._getCurrentUserInfo();

      if (!this.currentDraftOrder?.id) {
        throw new Error("No cart found to checkout");
      }

      const cartItems = this.currentDraftOrder.draftCartOrderItems || [];
      if (cartItems.length === 0) {
        throw new Error("Cart is empty");
      }

      // Validate all items have quantities
      const itemsWithoutQuantity = cartItems.filter(item => !item.unitQuantity || item.unitQuantity <= 0);
      if (itemsWithoutQuantity.length > 0) {
        throw new Error("Some cart items have no quantity set");
      }

      // Build the order payload matching the admin portal's CreateOrderWithInventoryAllocation
      const payload = {
        orderID: 0, // New order
        clientID: userInfo.clientId,
        clientSalesRepUserID: null, // Will be populated from client
        brandAmbassadorUserID: null,
        requestedDeliveryDate: checkoutData.requestedDeliveryDate || null,
        requestedDeliveryTime: checkoutData.requestedDeliveryTime || null,
        specialNotes: checkoutData.specialNotes || null,
        optPaymentTypeID: null, // Will use client default
        optOrderStatusID: 1, // Pending
        optFulfillmentStatusID: 1, // Pending
        draftCartOrderID: this.currentDraftOrder.id, // Link to draft for cleanup
        // Client credits the customer picked ({ clientCreditId, amount }); the server applies what fits.
        credits: checkoutData.credits || [],
        items: cartItems.map(item => {
          const productData = item.productSkuVariant;
          const unitsPerCase = productData?.unitsPerCase || 12;
          const priceInfo = this._resolveItemPrice(item, item.unitQuantity);

          // Apply single unit fee only for partial cases — and never when a winning
          // fee-waiver special list suppresses it (server re-resolves authoritatively).
          const shouldApplyFee =
            item.unitQuantity < unitsPerCase && !item.isSample && !priceInfo.suppressUnitFee;

          return {
            productSKUVariantID: item.productSkuVariantID,
            unitQuantity: item.unitQuantity,
            unitPrice: priceInfo.effectivePrice,
            singleUnitFee: shouldApplyFee ? parseFloat(item.singleUnitFee || 0) : 0,
            isSample: item.isSample || false
          };
        })
      };

      console.log("Creating order with payload:", JSON.stringify(payload, null, 2));

      const response = await apiService.post(
        "/storefront/checkout",
        payload
      );

      if (response.success || response.Success) {
        // Handle both old format (single order in data) and new format (split orders in createdOrders)
        const orderData = response.data || response.Data;
        const createdOrders = response.createdOrders || [];
        const wasSplit = response.wasSplit || false;

        // Back order info: fully out-of-stock but back-orderable items the backend split
        // into a back order (may accompany a normal order, or be the whole checkout).
        const hasBackOrder = response.hasBackOrder || false;
        const createdBackOrders = response.createdBackOrders || [];
        // One back order == one product, so the count is the number of back orders.
        const backOrderItemCount = createdBackOrders.length;
        const backOrderInfo = { hasBackOrder, createdBackOrders, backOrderItemCount };
        // Credits actually applied, one row per credit per order (what fit — may be less than picked).
        const appliedCredits = response.appliedCredits || [];

        // Clear the cart after successful order creation
        this.reset();

        if (wasSplit && createdOrders.length > 0) {
          // New split order response format
          const orderIds = createdOrders.map(o => o.order?.id).filter(Boolean);
          console.log(`Orders created successfully: ${orderIds.join(', ')} (split into ${createdOrders.length} invoices)`);

          if (appInsights) {
            appInsights.trackEvent({ name: 'OrderPlaced' }, {
              wasSplit: true,
              orderCount: createdOrders.length,
              orderIds: orderIds.join(','),
              itemCount: cartItems.length,
              draftCartOrderId: String(payload.draftCartOrderID || ''),
            });
          }

          return {
            success: true,
            wasSplit: true,
            message: response.message,
            orders: createdOrders.map(o => o.order),
            createdOrders: createdOrders, // Full response data for detailed info
            ...backOrderInfo,
            appliedCredits
          };
        } else if (orderData) {
          // Old single order response format
          console.log(`Order #${orderData.id} created successfully`);

          if (appInsights) {
            appInsights.trackEvent({ name: 'OrderPlaced' }, {
              wasSplit: false,
              orderCount: 1,
              orderId: String(orderData.id),
              itemCount: cartItems.length,
              draftCartOrderId: String(payload.draftCartOrderID || ''),
            });
          }

          return {
            success: true,
            order: orderData,
            ...backOrderInfo,
            appliedCredits
          };
        } else {
          // Success but no order data. Either a graceful edge case OR every item was
          // fully out of stock and back-ordered (Data = null + createdBackOrders).
          console.log(
            hasBackOrder
              ? 'All items placed on back order'
              : 'Order created successfully',
          );

          return {
            success: true,
            message:
              response.message ||
              (hasBackOrder ? 'Items placed on back order' : 'Order placed successfully'),
            ...backOrderInfo,
            appliedCredits
          };
        }
      } else {
        throw new Error(response.message || response.Message || "Failed to create order");
      }
    } catch (error) {
      console.error("Checkout error:", error);

      // Parse inventory errors if present
      const inventoryError = this._parseInventoryError(error);
      if (inventoryError) {
        // Create a custom error with the inventory details attached
        const inventoryErrorObj = new Error(inventoryError.message || 'Insufficient inventory');
        inventoryErrorObj.inventoryDetails = inventoryError.details;
        inventoryErrorObj.isInventoryError = true;
        throw inventoryErrorObj;
      }

      throw error;
    }
  }

  /**
   * Get current store info for cart operations
   */
  getCurrentStoreInfo() {
    const store = tokenManager.getCurrentStore();
    const company = tokenManager.getCurrentCompany();

    return {
      hasStore: !!store?.id,
      storeId: store?.id || null,
      storeName: store?.name || null,
      companyId: company?.id || null,
      companyName: company?.name || null
    };
  }

  /**
   * Force sync pending changes (useful for manual sync trigger)
   */
  async syncPendingChanges() {
    return this._processPendingChanges();
  }

  /**
   * Resolve unit price based on quantity and MOQ tiers
   * @param {object} item - Cart item with product data
   * @param {number} quantity - Quantity being purchased
   * @returns {object} { effectivePrice, originalPrice, isMOQ, savings }
   */
  _resolveItemPrice(item, quantity) {
    const product = item.productSkuVariant || item;
    const originalPrice = product.unitPrice || item.unitPrice || 0;
    const discountPrice = product.discountUnitPrice;
    const moqTiers = product.productSkuVariantMOQPricing || [];

    // Customer special price (Special Pricing module), looked up by variant id.
    const variantId = item.productSkuVariantID ?? product.id ?? product.productSkuVariantID;
    const special = getSpecialUnitPrice(variantId);

    // Base = lowest of regular / discount / special price.
    let basePrice = originalPrice;
    if (discountPrice && discountPrice > 0 && discountPrice < basePrice) basePrice = discountPrice;
    let specialWins = false;
    if (special != null && special > 0 && special < basePrice) {
      basePrice = special;
      specialWins = true;
    }

    // Highest applicable MOQ tier, if any.
    let applicableTier = null;
    if (moqTiers && moqTiers.length > 0) {
      const sortedTiers = [...moqTiers].sort((a, b) => b.unitQuantity - a.unitQuantity);
      for (const tier of sortedTiers) {
        if (quantity >= tier.unitQuantity) {
          applicableTier = tier;
          break;
        }
      }
    }

    // Lowest wins: a tier only applies if it beats the base (discount/special) price.
    if (applicableTier && applicableTier.unitPrice < basePrice) {
      return {
        effectivePrice: applicableTier.unitPrice,
        originalPrice,
        isMOQ: true,
        tierQuantity: applicableTier.unitQuantity,
        savings: originalPrice - applicableTier.unitPrice,
        suppressUnitFee: false // MOQ won, not the special list
      };
    }

    return {
      effectivePrice: basePrice,
      originalPrice,
      isMOQ: false,
      savings: originalPrice - basePrice,
      // Waive the broken-case fee only when the special price wins and its list flags it.
      suppressUnitFee: specialWins && getSpecialUnitFeeSuppressed(variantId)
    };
  }

  /**
   * Calculate cart totals with break-case fee logic and MOQ pricing
   */
  /**
   * One cart line's money: product subtotal (quantity × effective price, MOQ tiers and specials
   * applied), unit fees, and the subtotal at the original price.
   */
  _lineAmounts(item) {
    const totalUnits = item.unitQuantity || 0;

    // Resolve effective price based on quantity and MOQ tiers
    const priceInfo = this._resolveItemPrice(item, totalUnits);
    const subtotal = totalUnits * priceInfo.effectivePrice;

    // Break-case fee logic: Only apply fees if total units < unitsPerCase.
    // A fee-waiver special list (when it wins) zeroes the fee for this line.
    const unitsPerCase = item.productSkuVariant?.unitsPerCase || 1;
    const singleUnitFee = priceInfo.suppressUnitFee ? 0 : parseFloat(item.singleUnitFee || 0);

    // Only charge unit fees for partial cases (< full case)
    const fees = totalUnits < unitsPerCase ? totalUnits * singleUnitFee : 0;

    return { units: totalUnits, subtotal, fees, originalTotal: totalUnits * priceInfo.originalPrice };
  }

  /**
   * Money in the cart per brand, keyed by brand id: product (quantity × effective price) and
   * product + unit fees. A brand credit covers only its brand's product; a Stash House credit
   * can't cover a separately invoiced brand's product or its fees. Samples are left out: no credit
   * covers a sample.
   */
  calculateBrandTotals(items = []) {
    return items.reduce((byBrand, item) => {
      const brandId = item.productSkuVariant?.brandId;
      if (brandId == null || item.isSample) return byBrand;
      const { subtotal, fees } = this._lineAmounts(item);
      const brand = byBrand[brandId] || { product: 0, withFees: 0 };
      byBrand[brandId] = { product: brand.product + subtotal, withFees: brand.withFees + subtotal + fees };
      return byBrand;
    }, {});
  }

  calculateCartTotals(items = []) {
    return items.reduce(
      (totals, item) => {
        const { units: totalUnits, subtotal: itemSubtotal, fees: itemFees, originalTotal: itemOriginalTotal } =
          this._lineAmounts(item);

        // Calculate savings for this item
        const itemSavings = itemOriginalTotal - itemSubtotal;

        totals.subtotal += itemSubtotal;
        totals.fees += itemFees;
        totals.total += itemSubtotal + itemFees;
        totals.itemCount += 1;
        totals.unitCount += totalUnits;
        totals.totalSavings += itemSavings;
        totals.originalSubtotal += itemOriginalTotal;

        return totals;
      },
      {
        subtotal: 0,
        fees: 0,
        total: 0,
        itemCount: 0,
        unitCount: 0,
        totalSavings: 0,
        originalSubtotal: 0,
      },
    );
  }

  /**
   * Reset cart service (clear cached data)
   */
  reset() {
    this.currentDraftOrder = null;
    this.cache.clear();
    this.currentStoreId = null;
    this._clearPendingChanges();
    console.log("Cart service reset - cache cleared");
  }

  // ... [Keep all your other existing methods]

  /**
   * Get cart items with optimistic updates applied
   */
  async getCartItems() {
    try {
      const draftOrder = await this.getDraftCartOrder();
      return draftOrder.draftCartOrderItems || [];
    } catch (error) {
      console.error("Error getting cart items:", error);

      if (error.message?.includes("No store selected")) {
        return [];
      }

      return [];
    }
  }

  /**
   * Update product quantity in cart (for product tiles)
   * This handles the case/unit calculation logic
   * Creates cart lazily if adding first item
   */
  async updateProductQuantityInCart(
    productSkuVariantId,
    newTotalQuantity,
    productData = {},
  ) {
    try {
      // Get current cart state
      await this.getDraftCartOrder();
      const userInfo = this._getCurrentUserInfo();

      // Find existing item in current cart
      const existingItem = this.currentDraftOrder?.draftCartOrderItems?.find(
        (item) => item.productSkuVariantID === productSkuVariantId,
      );

      // Lazy cart creation: Create cart if adding items and no cart exists yet
      if (newTotalQuantity > 0 && !this.currentDraftOrder?.id) {
        console.log(`Creating cart lazily for store ${userInfo.storeName} on first item add`);
        await this.createNewDraftCartOrder();
      }

      if (newTotalQuantity <= 0) {
        // Remove item if exists
        if (existingItem && existingItem.id > 0) {
          this.pendingChanges.deleted.add(existingItem.id);
          // Remove from other pending changes
          this.pendingChanges.updated.delete(existingItem.id);
        }
        // Remove from added if it was a new item
        this.pendingChanges.added.delete(productSkuVariantId);
      } else {
        if (existingItem && existingItem.id > 0) {
          // Update existing real item
          this.pendingChanges.updated.set(existingItem.id, {
            unitQuantity: newTotalQuantity,
            unitPrice: productData.unitPrice || existingItem.unitPrice,
            singleUnitFee:
              productData.singleUnitFee || existingItem.singleUnitFee,
            isSample: productData.isSample || existingItem.isSample || false,
          });
        } else {
          // Add new item or update optimistic item
          this.pendingChanges.added.set(productSkuVariantId, {
            productSkuVariantID: productSkuVariantId,
            unitQuantity: newTotalQuantity,
            unitPrice: productData.unitPrice || 0,
            singleUnitFee: productData.singleUnitFee || 0,
            isSample: productData.isSample || false,
            productSkuVariant: productData.productSkuVariant || productData,
          });
        }
      }

      console.log(
        `Product quantity update queued for store ${userInfo.storeName}`,
      );

      // Process changes and wait for result
      await this._processPendingChanges();

      // Return the updated cart (with merged API response)
      return this._applyOptimisticUpdates(this.currentDraftOrder);
    } catch (error) {
      console.error("Error updating product quantity in cart:", error);
      throw error;
    }
  }

  // ... [Rest of your existing methods]
}

// Export singleton instance
export default new CartService();